/**
 * Regulatory Reconciliation View
 *
 * Policies reconciled against external guidance: RuleIQ (CMS final-rule
 * requirements) and Mandate (HPMS memos). Shows ONLY confirmed
 * non-compliance — every finding survived quote-grounding plus a skeptical
 * second judgment before it appears here, so an empty list is the expected,
 * healthy state. Each row quotes the obligation and the policy text that
 * falls short, side by side.
 */
import React, { useState, useEffect, useCallback } from 'react';
import InfoTooltip from '../../components/InfoTooltip';
import NotesModal from './NotesModal';
import {
  ScaleIcon,
  EnvelopeIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL;

const SEVERITY_CFG = {
  critical: { label: 'Critical', color: 'bg-red-100 text-red-800 border-red-300' },
  high:     { label: 'High',     color: 'bg-red-50 text-red-700 border-red-200' },
  medium:   { label: 'Medium',   color: 'bg-amber-50 text-amber-700 border-amber-200' },
  low:      { label: 'Low',      color: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const SOURCE_CFG = {
  ruleiq: { label: 'Final Rule',     Icon: ScaleIcon,    color: 'bg-amber-50 text-amber-700 border-amber-200' },
  memo:   { label: 'CMS Memo',       Icon: EnvelopeIcon, color: 'bg-sky-50 text-sky-700 border-sky-200' },
  smac:   { label: 'State Contract', Icon: ScaleIcon,    color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const STATUS_CFG = {
  open:         { label: 'Open',         color: 'bg-red-50 text-red-700 border-red-200' },
  acknowledged: { label: 'Acknowledged', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  resolved:     { label: 'Resolved',     color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  dismissed:    { label: 'Dismissed',    color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

export default function RegReconView({ onOpenPolicy }) {
  const [findings, setFindings] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [sourceFilter, setSourceFilter] = useState('');
  const [expanded, setExpanded] = useState({});
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState('');
  const [notesModal, setNotesModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set('status', statusFilter);
      if (sourceFilter) qs.set('source', sourceFilter);
      const [fRes, rRes] = await Promise.all([
        fetch(`${API}/api/ppm/reg-reconciliation?${qs}`, { credentials: 'include' }),
        fetch(`${API}/api/ppm/reg-reconciliation/runs`, { credentials: 'include' }),
      ]);
      const fData = await fRes.json();
      const rData = await rRes.json();
      if (fData.success) setFindings(fData.findings || []);
      if (rData.success) setRuns(rData.runs || []);
    } catch {}
    setLoading(false);
  }, [statusFilter, sourceFilter]);

  useEffect(() => { load(); }, [load]);

  const triggerScan = async () => {
    setScanning(true); setScanMsg('');
    try {
      const res = await fetch(`${API}/api/ppm/reg-reconciliation/scan`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      setScanMsg(data.success ? 'Scan started — this checks every policy against current rules and memos, give it a few minutes' : (data.message || 'Failed'));
      setTimeout(load, 45000);
    } catch { setScanMsg('Scan failed'); }
    setScanning(false);
  };

  const updateStatus = async (id, status, notes) => {
    try {
      await fetch(`${API}/api/ppm/reg-reconciliation/${id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, resolution_notes: notes || null }),
      });
      load();
    } catch {}
  };

  const latestRun = runs[0];
  const counts = findings.reduce((acc, f) => {
    acc[f.severity] = (acc[f.severity] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-800 tracking-tight">Rule & Memo Reconciliation</h2>
              <InfoTooltip
                title="Rule & Memo Reconciliation"
                width={400}
                content={
                  <>
                    Checks every active policy against <span className="text-white font-medium">current CMS guidance</span> —
                    the final-rule requirements RuleIQ extracted and the HPMS memos tracked in Mandate.
                    Only confirmed non-compliance appears: the policy must commit you to something the
                    guidance prohibits or has superseded, quoted verbatim on both sides. An empty list
                    means your policies don't contradict current guidance.
                  </>
                }
              />
            </div>
            <p className="text-slate-400 text-xs mt-0.5">
              {findings.length} {findings.length === 1 ? 'finding' : 'findings'}
              {counts.critical ? ` · ${counts.critical} critical` : ''}
              {counts.high ? ` · ${counts.high} high` : ''}
              {counts.medium ? ` · ${counts.medium} medium` : ''}
            </p>
          </div>
          <button onClick={triggerScan} disabled={scanning}
            className="px-5 py-2.5 rounded-xl bg-slate-800 text-white text-sm font-semibold hover:bg-slate-900 disabled:opacity-50 transition-colors">
            {scanning ? 'Starting...' : 'Run Scan Now'}
          </button>
        </div>

        {scanMsg && (
          <div className="mb-4 p-3 rounded-xl bg-orange-50 border border-orange-200 text-orange-700 text-xs">{scanMsg}</div>
        )}

        {latestRun && latestRun.finished_at && (
          <div className="mb-4 text-xs text-slate-400">
            Last scan: {new Date(latestRun.started_at).toLocaleString()} · {latestRun.policies_scanned} policies · {latestRun.pairs_compared} comparisons · {latestRun.findings_found} findings
            {latestRun.source_counts ? ` · sources: ${latestRun.source_counts.ruleiq ?? 0} rule requirements, ${latestRun.source_counts.memo ?? 0} memos` : ''}
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 mb-4 flex-wrap items-center">
          <div className="flex gap-1 p-1 bg-slate-100/80 rounded-xl">
            {['open', 'acknowledged', 'resolved', 'dismissed', 'all'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s === 'all' ? '' : s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize ${
                  (statusFilter || 'all') === s ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>{s}</button>
            ))}
          </div>
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
            className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
            <option value="">All sources</option>
            <option value="ruleiq">Final Rule (RuleIQ)</option>
            <option value="memo">CMS Memos (Mandate)</option>
            <option value="smac">State Contracts (SMAC)</option>
          </select>
        </div>

        {loading && (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 animate-pulse">
                <div className="h-4 bg-slate-100 rounded w-1/2 mb-2" />
                <div className="h-3 bg-slate-100 rounded w-1/3" />
              </div>
            ))}
          </div>
        )}

        {!loading && findings.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl flex items-center justify-center text-emerald-600">
              <CheckCircleIcon className="w-8 h-8" strokeWidth={1.75} />
            </div>
            <h3 className="text-base font-semibold text-slate-700 mb-1">No non-compliance found</h3>
            <p className="text-sm text-slate-400">{statusFilter === 'open' ? 'Your policies do not contradict the current rules and memos we checked. Run a scan to re-check.' : 'No findings match this filter.'}</p>
          </div>
        )}

        {!loading && findings.length > 0 && (
          <div className="space-y-3">
            {findings.map(f => {
              const sev = SEVERITY_CFG[f.severity] || SEVERITY_CFG.medium;
              const src = SOURCE_CFG[f.source] || SOURCE_CFG.ruleiq;
              const stat = STATUS_CFG[f.status] || STATUS_CFG.open;
              const isOpen = expanded[f.id];

              return (
                <div key={f.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <button onClick={() => setExpanded(e => ({ ...e, [f.id]: !e[f.id] }))}
                    className="w-full text-left p-5 hover:bg-slate-50/50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${sev.color}`}>
                            {sev.label}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${src.color}`}>
                            {src.Icon && <src.Icon className="w-3.5 h-3.5" strokeWidth={1.75} />} {src.label}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${stat.color}`}>{stat.label}</span>
                          {f.cfr_section && <span className="text-xs text-slate-500 font-mono">{f.cfr_section}</span>}
                          {f.topic && <span className="text-xs text-slate-500">· {f.topic}</span>}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-700 font-medium mb-1">
                          <button onClick={(e) => { e.stopPropagation(); onOpenPolicy(f.policy_id); }}
                            className="text-orange-700 hover:underline font-mono text-xs">
                            {f.policy_number}
                          </button>
                          <span className="text-xs text-slate-400 truncate">{f.source_label}</span>
                        </div>
                        <p className="text-sm text-slate-600 leading-relaxed">{f.summary}</p>
                      </div>
                      <svg className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-slate-100 p-5 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="rounded-xl border border-slate-200 bg-sky-50/30 p-4">
                          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">The guidance requires</div>
                          <p className="text-xs text-slate-700 font-mono whitespace-pre-wrap leading-relaxed">{f.obligation_quote}</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-red-50/30 p-4">
                          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">{f.policy_number} says</div>
                          <p className="text-xs text-slate-700 font-mono whitespace-pre-wrap leading-relaxed">{f.policy_quote}</p>
                        </div>
                      </div>

                      {f.recommended_resolution && (
                        <div className="rounded-xl border border-orange-200 bg-orange-50/50 p-4">
                          <div className="text-[10px] font-semibold text-orange-700 uppercase tracking-wide mb-1">Recommended resolution</div>
                          <p className="text-xs text-slate-700 leading-relaxed">{f.recommended_resolution}</p>
                        </div>
                      )}

                      {f.recommendation_id && f.status === 'open' && (
                        <div className="text-xs text-slate-500">
                          A policy recommendation has been created and assigned to the policy owner.
                        </div>
                      )}

                      {f.resolution_notes && (
                        <div className="text-xs text-slate-500 italic">Notes: {f.resolution_notes}</div>
                      )}

                      {f.status === 'open' && (
                        <div className="flex gap-2 pt-2">
                          <button onClick={() => updateStatus(f.id, 'acknowledged')}
                            className="px-4 py-2 rounded-xl bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 transition-colors">
                            Acknowledge
                          </button>
                          <button onClick={() => setNotesModal({
                              id: f.id, status: 'resolved', tone: 'emerald',
                              title: 'Mark finding resolved',
                              description: `${f.policy_number}: ${f.topic || f.summary}`,
                              placeholder: 'What changed to restore compliance? (e.g. how the policy was updated)',
                              confirmLabel: 'Mark Resolved',
                            })}
                            className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors">
                            Mark Resolved
                          </button>
                          <button onClick={() => setNotesModal({
                              id: f.id, status: 'dismissed', tone: 'orange',
                              title: 'Dismiss finding',
                              description: `${f.policy_number}: ${f.topic || f.summary}`,
                              placeholder: 'Why is this not real non-compliance?',
                              confirmLabel: 'Dismiss',
                            })}
                            className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50 transition-colors">
                            Dismiss (not an issue)
                          </button>
                        </div>
                      )}

                      {f.status !== 'open' && (
                        <button onClick={() => updateStatus(f.id, 'open', null)}
                          className="text-xs text-slate-500 hover:text-slate-700 font-medium">
                          Reopen
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <NotesModal
          open={!!notesModal}
          title={notesModal?.title}
          description={notesModal?.description}
          placeholder={notesModal?.placeholder}
          confirmLabel={notesModal?.confirmLabel}
          tone={notesModal?.tone}
          onClose={() => setNotesModal(null)}
          onConfirm={(notes) => { updateStatus(notesModal.id, notesModal.status, notes); setNotesModal(null); }}
        />
      </div>
    </div>
  );
}
